import axios from 'axios';

const API_BASE_URL = 'https://api.hajmetwalli.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Reduced to 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add retry logic for failed requests
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Retry on timeout or network error
    if ((error.code === 'ECONNABORTED' || error.message.includes('timeout')) && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('Retrying request due to timeout...');
      
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return api(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

// Menus
export const getMenus = () => api.get('/menus');
export const createMenu = (menuData) => api.post('/menus', menuData);

// Categories
export const getCategoriesByMenu = async (menuId) => {
  try {
    const response = await api.get(`/categories/${menuId}`);
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createCategory = async (categoryData) => {
  try {
    const response = await api.post('/categories', categoryData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Dish APIs
export const getDishesByCategory = async (categoryId) => {
  try {
    const response = await api.get(`/dishes/${categoryId}`);
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const createDish = async (dishData) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    formData.append('name', dishData.name);
    formData.append('price', dishData.price);
    formData.append('categoryId', dishData.categoryId);
    if (dishData.description) {
      formData.append('description', dishData.description);
    }
    
    // Add image file if it exists and is not a base64 string
    if (dishData.image && !dishData.image.startsWith('data:')) {
      formData.append('image', dishData.image);
    }
    
    const response = await api.post('/dishes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateDish = async (id, dishData) => {
  try {
    const formData = new FormData();
    
    // Add text fields
    formData.append('name', dishData.name);
    formData.append('price', dishData.price);
    formData.append('categoryId', dishData.categoryId);
    if (dishData.description) {
      formData.append('description', dishData.description);
    }
    
    // Add image file if it exists and is not a base64 string
    if (dishData.image && !dishData.image.startsWith('data:')) {
      formData.append('image', dishData.image);
    }
    
    const response = await api.put(`/dishes/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteDish = async (id) => {
  try {
    const response = await api.delete(`/dishes/${id}`);
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteCategory = async (id) => {
  try {
    const response = await api.delete(`/categories/${id}`);
    return response;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Additional endpoints for Reports and Add Item pages
export const getAllCategories = async () => {
  try {
    const response = await api.get('/categories');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getAllDishes = async () => {
  try {
    const response = await api.get('/dishes/all');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getDishesByMenu = async (menuId) => {
  try {
    console.log(`Making API call to: /dishes/menu/${menuId}`);
    const response = await api.get(`/dishes/menu/${menuId}`);
    console.log('API response received:', response);
    return response;
  } catch (error) {
    console.error('API error in getDishesByMenu:', error);
    
    // Handle different types of errors
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - server might be slow');
      throw new Error('Server response timeout. Please try again.');
    } else if (error.response) {
      console.error('Server responded with error:', error.response.status, error.response.data);
      throw new Error(`Server error: ${error.response.data?.error || 'Unknown error'}`);
    } else if (error.request) {
      console.error('No response received from server');
      throw new Error('Cannot connect to server. Please check your connection.');
    } else {
      console.error('Request setup error:', error.message);
      throw new Error('Request failed. Please try again.');
    }
  }
};

export const getDishById = async (dishId) => {
  try {
    console.log(`Making API call to: /dish/${dishId}`);
    const response = await api.get(`/dish/${dishId}`);
    console.log('API response received:', response);
    return response;
  } catch (error) {
    console.error('API error in getDishById:', error);
    
    // Handle different types of errors
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - server might be slow');
      throw new Error('Server response timeout. Please try again.');
    } else if (error.response) {
      console.error('Server responded with error:', error.response.status, error.response.data);
      throw new Error(`Server error: ${error.response.data?.error || 'Unknown error'}`);
    } else if (error.request) {
      console.error('No response received from server');
      throw new Error('Cannot connect to server. Please check your connection.');
    } else {
      console.error('Request setup error:', error.message);
      throw new Error('Request failed. Please try again.');
    }
  }
};

// Settings APIs
export const getSettings = async () => {
  try {
    const response = await api.get('/settings');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateSettings = async (settingsData) => {
  try {
    const response = await api.put('/settings', settingsData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export default api;
